import { Spacing } from '@persona-id/design-tokens';
import produce from 'immer';
import { Fragment, MutableRefObject, ReactNode, RefObject, useState } from 'react';
import { useSelector } from 'react-redux';
import { FormSection } from 'redux-form';
import styled from 'styled-components';

import { TOOLBAR_TRANSITION } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditorToolbar';
import useIsEditableWithOptions, {
  EditorAccessTier,
} from 'dashboard/apps/VerificationTemplateEditor/hooks/useIsEditableWithOptions';
import ClassificationsConfig from 'dashboard/components/ClassificationsConfig';
import ClassificationPanel from 'dashboard/components/ClassificationsConfig/ClassificationPanel';
import DocumentFields from 'dashboard/components/DocumentFields';
import DocumentFieldPanel from 'dashboard/components/DocumentFields/DocumentFieldPanel';
import DocumentTesterPanel from 'dashboard/components/DocumentTesterPanel';
import {
  addExtractionToVerificationChecks,
  deleteExtractionFromVerificationChecks,
} from 'dashboard/components/ExtractionPanel';
import ExtractionPanel from 'dashboard/components/ExtractionPanel/ExtractionPanel';
import ExtractionsTableWithMultipleFieldWritebacks from 'dashboard/components/ExtractionPanel/ExtractionsTableWithMultipleFieldWritebacks';
import { extractionUsesAi } from 'dashboard/components/ExtractionPanel/util';
import FeatureFlagged from 'dashboard/components/FeatureFlagged';
import InsightPanel from 'dashboard/components/InsightPanel';
import InsightsTable from 'dashboard/components/InsightPanel/InsightsTable';
import DocumentVerificationTemplateHeadSection from 'dashboard/forms/FormVerificationDocumentTemplateConfig/DocumentVerificationTemplateHeadSection';
import DocumentFormVerificationTemplateChecks from 'dashboard/forms/FormVerificationTemplateChecks/DocumentFormVerificationTemplateChecks';
import {
  Classification,
  ClassificationMethod,
  ExtractionRequest,
  FieldWriteBackRequest,
  FieldWriteBackSourceType,
  InsightRequest,
  ToggleOption,
} from 'dashboard/models/document';
import {
  MatchRequirementKeys,
  MatchRequirementProfile,
} from 'dashboard/models/match-requirement-profile';
import { CustomerTier } from 'dashboard/models/organization';
import { RequiredExtraction } from 'dashboard/models/verification-check';
import { isDocumentExtractionCheck } from 'dashboard/models/verification-check-config/document-extracted-properties-detection';
import { isDocumentTypeCheck } from 'dashboard/models/verification-check-config/document-type-detection';
import {
  VerificationTemplateVersion,
  VerificationTemplateVersionDocument,
} from 'dashboard/models/verification-template-version';
import { RootState } from 'dashboard/redux/store';
import { Z_INDICES } from 'dashboard/styles/variables';
import Banner from 'design-system/components/Banner';
import Button from 'design-system/components/Button';
import Spacer from 'design-system/components/Spacer';
import { Heading, Paragraph } from 'design-system/components/Text';
import { FieldSchema } from 'lib/fields';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';
import useIsPersonerd from 'lib/hooks/useIsPersonerd';
import usePermission from 'lib/hooks/usePermission';
import useToggle from 'lib/hooks/useToggle';
import { Permission } from 'lib/permissions';
import { VerificationStatus } from 'lib/verification';
import { CheckName } from 'lib/verificationCheck';

export enum DocumentsSection {
  General = 'general',
  Classifications = 'classifications',
  Extractions = 'extractions',
  Insights = 'insights',
  DocumentFields = 'documentFields',
  VerificationChecks = 'verificationChecks',
  DocumentTester = 'documentTester',
}

const DOCUMENT_AI_BLOCK_LIMIT = 10;
export const DOCUMENT_AI_BLOCK_MESSAGE = `A maximum of ${DOCUMENT_AI_BLOCK_LIMIT} AI extractions and insights can be configured for a document.`;
const DOCUMENT_AI_WARNING_LIMIT = 7;

const StepReferences = styled(Paragraph).attrs(() => ({ size: 'md' }))`
  color: ${({ theme }) => theme.color.text.tertiary};
`;

const SectionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: ${Spacing[4]};
`;

const DocumentAiBanner = styled(Banner)`
  margin: 0 ${Spacing[2]};
  position: sticky;
  top: 0;
  z-index: ${Z_INDICES.banner};
`;

const RightColumn = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  flex: auto;
  width: 100%;
  padding: 0 ${Spacing[5]};
  overflow-y: auto;
  background-color: ${({ theme }) => theme.color.background.secondary};
`;

const ControlPanelContainer = styled.div<{ visible: boolean }>`
  background-color: ${({ theme }) => theme.color.background.primary};
  overflow-y: auto;
  overflow-x: hidden;
  transition: flex ${TOOLBAR_TRANSITION};
  border-left: ${({ visible, theme }) => visible && `1px solid ${theme.color.border.primary};`};
  flex: ${({ visible }) => (visible ? '1 0 400px' : '1 0 0px')};
  & > div {
    height: 100%;
  }
`;

const HeaderContainer = styled.div`
  width: auto;
  display: flex;
  justify-content: space-between;
`;

const countDocumentAiFeatures = (
  vtmplVersion: VerificationTemplateVersionDocument,
  documentSection: DocumentsSection,
) => {
  const { classifications, extractions, insights } = vtmplVersion.attributes;

  switch (documentSection) {
    case DocumentsSection.Classifications:
      return classifications.filter(
        (classification) => classification['classification-method'] === ClassificationMethod.Ai,
      ).length;
    case DocumentsSection.Extractions:
      return extractions.filter((extraction) => extractionUsesAi(extraction)).length;
    case DocumentsSection.Insights:
      return insights.length;
    default:
      return 0;
  }
};

const blockDocumentAiFeatures = (vtmplVersion: VerificationTemplateVersionDocument) => {
  const extractionCount = countDocumentAiFeatures(vtmplVersion, DocumentsSection.Extractions);
  const insightCount = countDocumentAiFeatures(vtmplVersion, DocumentsSection.Insights);

  return extractionCount + insightCount >= DOCUMENT_AI_BLOCK_LIMIT;
};

const warnDocumentAiFeatures = (vtmplVersion: VerificationTemplateVersionDocument) => {
  const classificationCount = countDocumentAiFeatures(
    vtmplVersion,
    DocumentsSection.Classifications,
  );
  const extractionCount = countDocumentAiFeatures(vtmplVersion, DocumentsSection.Extractions);
  const insightCount = countDocumentAiFeatures(vtmplVersion, DocumentsSection.Insights);

  return classificationCount * 0.1 + extractionCount + insightCount >= DOCUMENT_AI_WARNING_LIMIT;
};

const addDocumentField = (
  vtmplVersion: VerificationTemplateVersionDocument,
  documentField: FieldSchema,
) => {
  const updatedVtmplVersion = produce(vtmplVersion, (draft) => {
    draft?.attributes?.['field-schemas']?.push(documentField);
  });

  return updatedVtmplVersion;
};

const updateDocumentField = (
  vtmplVersion: VerificationTemplateVersionDocument,
  documentField: FieldSchema,
  index: number,
) => {
  const updatedVtmplVersion = produce(vtmplVersion, (draft) => {
    const oldDocumentFieldKey = draft?.attributes?.['field-schemas']?.[index]?.key;

    if (draft?.attributes?.['field-schemas']?.[index] != null) {
      draft.attributes['field-schemas'][index] = documentField;
    }

    // update any field write backs tied to the updated field
    if (
      oldDocumentFieldKey !== documentField.key &&
      draft?.attributes?.['field-write-backs'] != null
    ) {
      draft.attributes['field-write-backs'] = draft.attributes['field-write-backs'].map(
        (fieldWriteBack) => {
          if (fieldWriteBack?.['field-name'] === oldDocumentFieldKey) {
            return { ...fieldWriteBack, 'field-name': documentField.key };
          }
          return fieldWriteBack;
        },
      );
    }
  });

  return updatedVtmplVersion;
};

const deleteDocumentField = (vtmplVersion: VerificationTemplateVersionDocument, index: number) => {
  const documentField = vtmplVersion.attributes['field-schemas']?.[index];
  const updatedVtmplVersion = produce(vtmplVersion, (draft) => {
    draft.attributes['field-schemas']?.splice(index, 1);

    draft.attributes['field-write-backs'] = draft.attributes['field-write-backs']?.filter(
      (fieldWriteBack) => fieldWriteBack['field-name'] !== documentField?.key,
    );
  });

  return updatedVtmplVersion;
};

const toggleClassificationAccepted = (
  vtmplVersion: VerificationTemplateVersionDocument,
  classification: Classification,
  accepted: boolean,
) => {
  if (classification['toggle-option'] === ToggleOption.Disabled) return vtmplVersion;

  const updatedVtmplVersion = produce(vtmplVersion, (draft) => {
    const existingDocumentTypeDetection = draft.attributes['verification-checks'].find(
      (v) => v.check === CheckName.DocumentTypeDetection,
    );
    let documentTypeDetection =
      existingDocumentTypeDetection && isDocumentTypeCheck(existingDocumentTypeDetection)
        ? existingDocumentTypeDetection
        : undefined;
    if (documentTypeDetection == null) {
      documentTypeDetection = {
        check: CheckName.DocumentTypeDetection,
        'on-failed': VerificationStatus.Failed,
        'toggle-option': 'enabled',
        'type-acceptance-criteria': [],
        'type-rejection-criteria': [],
        'accepted-document-types': [],
      };
      draft.attributes['verification-checks'].push(documentTypeDetection);
    }
    documentTypeDetection['toggle-option'] = 'enabled';
    documentTypeDetection['on-failed'] = VerificationStatus.Failed;

    const acceptanceIndex = documentTypeDetection['type-acceptance-criteria'].findIndex(
      (type) => type['type-name'] === classification.key,
    );
    const rejectionIndex = documentTypeDetection['type-rejection-criteria'].findIndex(
      (type) => type['type-name'] === classification.key,
    );
    const criteria = { 'type-name': classification.key, 'specified-languages': [] };

    if (accepted) {
      if (acceptanceIndex === -1) documentTypeDetection['type-acceptance-criteria'].push(criteria);
      if (rejectionIndex !== -1)
        documentTypeDetection['type-rejection-criteria'].splice(rejectionIndex, 1);
    } else {
      if (rejectionIndex === -1) documentTypeDetection['type-rejection-criteria'].push(criteria);
      if (acceptanceIndex !== -1)
        documentTypeDetection['type-acceptance-criteria'].splice(acceptanceIndex, 1);

      // todo: remove this once migration to 'type-acceptance-criteria' is complete
      documentTypeDetection['accepted-document-types'] = documentTypeDetection[
        'accepted-document-types'
      ].filter((type) => type !== classification.key);
    }
  });

  return updatedVtmplVersion;
};

const removeClassificationAcceptance = (
  vtmplVersion: VerificationTemplateVersionDocument,
  classification: Classification,
) => {
  const updatedVtmplVersion = produce(vtmplVersion, (draft) => {
    const existingDocumentTypeDetection = draft.attributes['verification-checks'].find(
      (v) => v.check === CheckName.DocumentTypeDetection,
    );
    const documentTypeDetection =
      existingDocumentTypeDetection && isDocumentTypeCheck(existingDocumentTypeDetection)
        ? existingDocumentTypeDetection
        : undefined;
    if (documentTypeDetection == null) return;

    if (
      documentTypeDetection['type-acceptance-criteria'].length +
        documentTypeDetection['type-rejection-criteria'].length +
        documentTypeDetection['accepted-document-types'].length !==
      0
    ) {
      documentTypeDetection['type-acceptance-criteria'] = documentTypeDetection[
        'type-acceptance-criteria'
      ].filter((type) => type['type-name'] !== classification.key);
      documentTypeDetection['type-rejection-criteria'] = documentTypeDetection[
        'type-rejection-criteria'
      ].filter((type) => type['type-name'] !== classification.key);

      // todo(documents): remove this once migration to 'type-acceptance-criteria' is complete
      documentTypeDetection['accepted-document-types'] = documentTypeDetection[
        'accepted-document-types'
      ].filter((type) => type !== classification.key);

      if (
        documentTypeDetection['type-acceptance-criteria'].length +
          documentTypeDetection['type-rejection-criteria'].length +
          documentTypeDetection['accepted-document-types'].length ===
        0
      ) {
        documentTypeDetection['on-failed'] = VerificationStatus.Passed;
      }
    }
  });

  return updatedVtmplVersion;
};

const addClassification = (
  vtmplVersion: VerificationTemplateVersionDocument,
  classification: Classification,
) => {
  const index = vtmplVersion.attributes.classifications.findIndex(
    (c) =>
      c.key === classification.key &&
      c['definition-type'] === classification['definition-type'] &&
      c['classification-method'] === classification['classification-method'],
  );
  if (vtmplVersion.attributes.classifications[index]?.['toggle-option'] === ToggleOption.Enabled)
    return vtmplVersion;

  let updatedVtmplVersion = toggleClassificationAccepted(vtmplVersion, classification, true);
  updatedVtmplVersion = produce(updatedVtmplVersion, (draft) => {
    if (index === -1) {
      draft.attributes.classifications.push(classification);
    } else {
      draft.attributes.classifications[index] = classification;
    }
  });

  return updatedVtmplVersion;
};

const updateClassification = (
  vtmplVersion: VerificationTemplateVersionDocument,
  classification: Classification,
  index: number,
) => {
  let updatedVtmplVersion = vtmplVersion;

  const oldClassification = updatedVtmplVersion.attributes.classifications[index];
  const existingDocumentTypeDetection = updatedVtmplVersion.attributes['verification-checks'].find(
    (v) => v.check === CheckName.DocumentTypeDetection,
  );
  const documentTypeDetection =
    existingDocumentTypeDetection && isDocumentTypeCheck(existingDocumentTypeDetection)
      ? existingDocumentTypeDetection
      : undefined;
  if (oldClassification.key !== classification.key && documentTypeDetection != null) {
    updatedVtmplVersion = removeClassificationAcceptance(updatedVtmplVersion, oldClassification);

    const accepted =
      documentTypeDetection['type-acceptance-criteria'].some(
        (type) => type['type-name'] === oldClassification.key,
      ) ||
      documentTypeDetection['accepted-document-types'].some(
        (type) => type === oldClassification.key,
      );

    updatedVtmplVersion = toggleClassificationAccepted(
      updatedVtmplVersion,
      classification,
      accepted,
    );
  }

  updatedVtmplVersion = produce(updatedVtmplVersion, (draft) => {
    draft.attributes.classifications[index] = classification;
  });

  return updatedVtmplVersion;
};

const deleteClassification = (vtmplVersion: VerificationTemplateVersionDocument, index: number) => {
  const classification = vtmplVersion.attributes.classifications[index];
  let updatedVtmplVersion = removeClassificationAcceptance(vtmplVersion, classification);
  updatedVtmplVersion = produce(updatedVtmplVersion, (draft) => {
    draft.attributes.classifications.splice(index, 1);
  });
  return updatedVtmplVersion;
};

const addExtraction = (
  extractionRequest: ExtractionRequest,
  fieldWriteBackRequests: FieldWriteBackRequest[],
  documentFieldRequests: FieldSchema[] | null,
  isRequired: boolean,
  vtmplVersion: VerificationTemplateVersionDocument,
) => {
  const updatedVtmplVersion = produce(vtmplVersion, (draft) => {
    draft.attributes.extractions.push(extractionRequest);
    if (isRequired && draft?.attributes?.['verification-checks'] != null) {
      draft.attributes['verification-checks'] = addExtractionToVerificationChecks(
        vtmplVersion,
        extractionRequest,
      );
    }
    if (draft?.attributes?.['field-write-backs'] != null) {
      draft.attributes['field-write-backs'] = fieldWriteBackRequests;
    }
    if (documentFieldRequests != null) {
      documentFieldRequests.forEach((req) => {
        draft?.attributes?.['field-schemas']?.push(req);
      });
    }
  });

  return updatedVtmplVersion;
};

const updateExtraction = (
  extractionRequest: ExtractionRequest,
  fieldWriteBackRequests: FieldWriteBackRequest[],
  documentFieldRequests: FieldSchema[] | null,
  isRequired: boolean,
  vtmplVersion: VerificationTemplateVersionDocument,
  index: number,
) => {
  const updatedVtmplVersion = produce(vtmplVersion, (draft) => {
    if (draft?.attributes?.['verification-checks'] != null) {
      draft.attributes['verification-checks'] = deleteExtractionFromVerificationChecks(
        draft,
        index,
      );
      if (isRequired) {
        draft.attributes['verification-checks'] = addExtractionToVerificationChecks(
          draft,
          extractionRequest,
        );
      }
    }
    if (draft?.attributes?.extractions?.[index] != null) {
      draft.attributes.extractions[index] = extractionRequest;
    }
    if (draft?.attributes?.['field-write-backs'] != null) {
      draft.attributes['field-write-backs'] = fieldWriteBackRequests;
    }
    if (documentFieldRequests != null) {
      documentFieldRequests.forEach((req) => {
        draft?.attributes?.['field-schemas']?.push(req);
      });
    }
  });

  return updatedVtmplVersion;
};

const deleteExtraction = (vtmplVersion: VerificationTemplateVersionDocument, index: number) => {
  const extraction = vtmplVersion.attributes.extractions[index];
  const updatedVtmplVersion = produce(vtmplVersion, (draft) => {
    draft.attributes['verification-checks'] = deleteExtractionFromVerificationChecks(draft, index);
    draft.attributes['field-write-backs'] = draft.attributes['field-write-backs']
      ?.map((fieldWriteBack) => ({
        ...fieldWriteBack,
        options: fieldWriteBack.options.filter(
          (option) =>
            // Ensure that we retain any other field write back options that are not related to the extraction
            option['source-type'] !== FieldWriteBackSourceType.Extraction ||
            (option['source-type'] === FieldWriteBackSourceType.Extraction &&
              option['source-key'] !== extraction['field-name']),
        ),
      }))
      .filter((fieldWriteBack) => fieldWriteBack.options.length > 0);

    draft.attributes.extractions.splice(index, 1);
  });

  return updatedVtmplVersion;
};

const addInsight = (
  insightRequest: InsightRequest,
  fieldWriteBackRequests: FieldWriteBackRequest[],
  documentFieldRequest: FieldSchema | null,
  vtmplVersion: VerificationTemplateVersionDocument,
) => {
  const updatedVtmplVersion = produce(vtmplVersion, (draft) => {
    draft?.attributes?.insights?.push(insightRequest);
    if (draft?.attributes?.['field-write-backs'] != null) {
      draft.attributes['field-write-backs'] = fieldWriteBackRequests;
    }
    if (documentFieldRequest != null) {
      draft?.attributes?.['field-schemas']?.push(documentFieldRequest);
    }
  });

  return updatedVtmplVersion;
};

const updateInsight = (
  insightRequest: InsightRequest,
  fieldWriteBackRequests: FieldWriteBackRequest[],
  documentFieldRequest: FieldSchema | null,
  vtmplVersion: VerificationTemplateVersionDocument,
  index: number,
) => {
  const updatedVtmplVersion = produce(vtmplVersion, (draft) => {
    if (draft?.attributes?.insights?.[index] != null) {
      draft.attributes.insights[index] = insightRequest;
    }
    if (draft?.attributes?.['field-write-backs'] != null) {
      draft.attributes['field-write-backs'] = fieldWriteBackRequests;
    }
    if (documentFieldRequest != null) {
      draft?.attributes?.['field-schemas']?.push(documentFieldRequest);
    }
  });

  return updatedVtmplVersion;
};

const deleteInsight = (vtmplVersion: VerificationTemplateVersionDocument, index: number) => {
  const insight = vtmplVersion.attributes.insights[index];
  const updatedVtmplVersion = produce(vtmplVersion, (draft) => {
    draft.attributes['field-write-backs'] = draft.attributes['field-write-backs']
      ?.map((fieldWriteBack) => ({
        ...fieldWriteBack,
        options: fieldWriteBack.options.filter(
          (option) =>
            // Ensure that we retain any other field write back options that are not related to the insight
            option['source-type'] !== FieldWriteBackSourceType.Insight ||
            (option['source-type'] === FieldWriteBackSourceType.Insight &&
              option['source-key'] !== insight.key),
        ),
      }))
      .filter((fieldWriteBack) => fieldWriteBack.options.length > 0);

    draft.attributes.insights.splice(index, 1);
  });

  return updatedVtmplVersion;
};

interface FormVerificationDocumentTemplateConfigProps {
  vtmplVersion: VerificationTemplateVersionDocument;
  linkedStepsElement: ReactNode;
  matchRequirementProfiles: MatchRequirementProfile[];
  formSectionName: string;
  selectedMatchRequirement: MatchRequirementKeys | null;
  hasEditPermission: boolean;
  hasInquiryTemplate: boolean;
  handleUpdateVerificationTemplateVersion: (vtmplVersion: VerificationTemplateVersion) => void;
  documentVerificationSectionRefs?: MutableRefObject<
    Record<DocumentsSection, RefObject<HTMLDivElement>>
  >;
  title?: string;
  rightColumnRef?: RefObject<HTMLDivElement>;
  fieldSchema?: FieldSchema[];
}

const FormVerificationDocumentTemplateConfig = ({
  vtmplVersion,
  linkedStepsElement,
  matchRequirementProfiles,
  formSectionName,
  selectedMatchRequirement,
  hasEditPermission,
  hasInquiryTemplate,
  handleUpdateVerificationTemplateVersion,
  documentVerificationSectionRefs,
  title,
  rightColumnRef,
  fieldSchema,
}: FormVerificationDocumentTemplateConfigProps) => {
  const editableUngated = useIsEditableWithOptions() && hasEditPermission;
  const editableForBasicTier =
    useIsEditableWithOptions({ accessTier: EditorAccessTier.VerificationsBasic }) &&
    hasEditPermission;
  const editableInternal = useIsEditableWithOptions({ internalOnly: true }) && hasEditPermission;

  const [documentAiBanner, { disable: disableDocumentAiBanner }] = useToggle(true);
  const canViewDocumentExtractionConfig = usePermission(
    Permission.InquiryTemplateDocumentExtractionConfigView,
  );
  const canEditDocumentExtractionConfig = usePermission(
    Permission.InquiryTemplateDocumentExtractionConfigEdit,
  );
  const organizationIsManagedTier1OrManagedTier2 = useSelector<RootState, boolean>(
    (state) =>
      state.organization.organization?.attributes['customer-tier'] === CustomerTier.Tier1 ||
      state.organization.organization?.attributes['customer-tier'] === CustomerTier.Tier2,
  );
  const editable = editableInternal || (editableForBasicTier && canEditDocumentExtractionConfig);

  const [documentPanel, setDocumentPanel] = useState<{
    type: DocumentsSection;
    index: number | null;
    vtmplId: string;
  } | null>(null);
  const shouldDisplayDocumentPanel = documentPanel != null;
  const handleOpenDocumentPanel = (type: DocumentsSection, index?: number | null) => {
    if (typeof index === 'undefined') {
      handleCloseDocumentPanel();
    } else {
      setDocumentPanel({ type, index, vtmplId: vtmplVersion.id });
    }
  };
  const handleCloseDocumentPanel = () => {
    setDocumentPanel(null);
  };

  const getSelectedIndex = (documentSection: DocumentsSection) =>
    documentPanel?.type === documentSection ? documentPanel.index : null;
  const handleOnSelect = (documentSection: DocumentsSection, index?: number | null) => {
    handleOpenDocumentPanel(documentSection, index);
  };

  const scrollToDocumentsSection = (documentsSection: DocumentsSection) => {
    const extractionsRef = documentVerificationSectionRefs?.current[documentsSection];
    if (extractionsRef) {
      extractionsRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const isForceEnableVtvConfigEditing = useFeatureFlag(
    'dashboard:ite-force-enable-vtv-config-editing',
  );
  const isPersonerd = useIsPersonerd();
  const canEditVerificationChecks =
    editableUngated &&
    (!organizationIsManagedTier1OrManagedTier2 || isForceEnableVtvConfigEditing || isPersonerd);

  const {
    'field-schemas': documentFields = [],
    'field-write-backs': fieldWriteBacks = [],
    classifications,
    extractions,
    insights,
    'insight-limit': insightLimit,
    'verification-checks': verificationChecks,
  } = vtmplVersion.attributes;

  const existingExtractedPropertiesDetection = verificationChecks.find(
    (v) => v.check === CheckName.DocumentExtractedPropertiesDetection,
  );
  const extractedPropertiesDetection =
    existingExtractedPropertiesDetection &&
    isDocumentExtractionCheck(existingExtractedPropertiesDetection)
      ? existingExtractedPropertiesDetection
      : undefined;

  const requiredExtractions = extractedPropertiesDetection?.['required-extractions'] || [];
  const requiredExtractionsFieldNames = requiredExtractions.map(
    (requiredExtraction: RequiredExtraction) => requiredExtraction['field-name'],
  );

  const existingDocumentTypeDetection = verificationChecks.find(
    (v) => v.check === CheckName.DocumentTypeDetection,
  );
  const documentTypeDetection =
    existingDocumentTypeDetection && isDocumentTypeCheck(existingDocumentTypeDetection)
      ? existingDocumentTypeDetection
      : undefined;
  const acceptedClassifications = (documentTypeDetection?.['type-acceptance-criteria'] || [])
    .map((type) => type['type-name'])
    // todo(documents): remove this once migration to 'type-acceptance-criteria' is complete
    .concat(documentTypeDetection?.['accepted-document-types'] || [])
    .map((documentType) =>
      classifications.findIndex((classification) => classification.key === documentType),
    );

  const handleAddDocumentField = (documentField: FieldSchema) =>
    handleUpdateVerificationTemplateVersion(addDocumentField(vtmplVersion, documentField));
  const handleUpdateDocumentField = (documentField: FieldSchema) =>
    handleUpdateVerificationTemplateVersion(
      updateDocumentField(vtmplVersion, documentField, documentPanel!.index!),
    );
  const handleDeleteDocumentField = (index: number) =>
    handleUpdateVerificationTemplateVersion(deleteDocumentField(vtmplVersion, index));

  const handleAddClassification = (classification: Classification) =>
    handleUpdateVerificationTemplateVersion(addClassification(vtmplVersion, classification));
  const handleUpdateClassification = (classification: Classification) =>
    handleUpdateVerificationTemplateVersion(
      updateClassification(vtmplVersion, classification, documentPanel!.index!),
    );
  const handleDeleteClassification = (index: number) =>
    handleUpdateVerificationTemplateVersion(deleteClassification(vtmplVersion, index));
  const handleReplaceClassifications = (indices: number[], classifications: Classification[]) => {
    let updatedVtmplVersion = vtmplVersion;
    updatedVtmplVersion = indices
      .sort((a, b) => b - a)
      .reduce(
        (vtmplVersion, index) => deleteClassification(vtmplVersion, index),
        updatedVtmplVersion,
      );
    updatedVtmplVersion = classifications.reduce(
      (vtmplVersion, classification) => addClassification(vtmplVersion, classification),
      updatedVtmplVersion,
    );
    handleUpdateVerificationTemplateVersion(updatedVtmplVersion);
  };
  const handleToggleClassificationAccepted = (classification: Classification, accepted: boolean) =>
    handleUpdateVerificationTemplateVersion(
      toggleClassificationAccepted(vtmplVersion, classification, accepted),
    );

  const handleAddExtraction = (
    extractionRequest: ExtractionRequest,
    fieldWriteBackRequests: FieldWriteBackRequest[],
    documentFieldRequests: FieldSchema[] | null,
    isRequired: boolean,
  ) =>
    handleUpdateVerificationTemplateVersion(
      addExtraction(
        extractionRequest,
        fieldWriteBackRequests,
        documentFieldRequests,
        isRequired,
        vtmplVersion,
      ),
    );
  const handleUpdateExtraction = (
    extractionRequest: ExtractionRequest,
    fieldWriteBackRequests: FieldWriteBackRequest[],
    documentFieldRequests: FieldSchema[] | null,
    isRequired: boolean,
  ) =>
    handleUpdateVerificationTemplateVersion(
      updateExtraction(
        extractionRequest,
        fieldWriteBackRequests,
        documentFieldRequests,
        isRequired,
        vtmplVersion,
        documentPanel!.index!,
      ),
    );
  const handleDeleteExtraction = (index: number) =>
    handleUpdateVerificationTemplateVersion(deleteExtraction(vtmplVersion, index));

  const handleAddInsight = (
    insightRequest: InsightRequest,
    fieldWriteBackRequests: FieldWriteBackRequest[],
    documentFieldRequest: FieldSchema | null,
  ) =>
    handleUpdateVerificationTemplateVersion(
      addInsight(insightRequest, fieldWriteBackRequests, documentFieldRequest, vtmplVersion),
    );
  const handleUpdateInsight = (
    insightRequest: InsightRequest,
    fieldWriteBackRequests: FieldWriteBackRequest[],
    documentFieldRequest: FieldSchema | null,
  ) =>
    handleUpdateVerificationTemplateVersion(
      updateInsight(
        insightRequest,
        fieldWriteBackRequests,
        documentFieldRequest,
        vtmplVersion,
        documentPanel!.index!,
      ),
    );
  const handleDeleteInsight = (index: number) =>
    handleUpdateVerificationTemplateVersion(deleteInsight(vtmplVersion, index));

  return (
    <>
      <RightColumn ref={rightColumnRef}>
        {title ? (
          <>
            <Spacer height={3} />
            <HeaderContainer>
              <Heading size='sm'>{title}</Heading>
              <FeatureFlagged featureFlag='dashboard:fle-enable-document-tester'>
                <Button
                  id='form-verification-document-template-config__upload-and-test-documents-button'
                  variant='basic'
                  width='auto'
                  onClick={() => handleOnSelect(DocumentsSection.DocumentTester, null)}
                >
                  Upload & test documents
                </Button>
              </FeatureFlagged>
            </HeaderContainer>
            <Spacer height={3} />
          </>
        ) : (
          <Spacer height={3} />
        )}
        <FormSection
          // when this component is being used by the InquiryTemplateEditor, the vtv will be under 'verificationTemplateVersions.id'
          // when this component is being used by the VerificationTemplateEditor, the vtv will be under 'verificationTemplateVersion'
          name={formSectionName}
          component={Fragment}
        >
          {documentAiBanner && warnDocumentAiFeatures(vtmplVersion) && (
            <>
              <DocumentAiBanner variant='warning' onClose={disableDocumentAiBanner}>
                Too many AI features may cause increased latency. Consider simplifying your template
                to ensure optimal performance.
              </DocumentAiBanner>
              <Spacer height={2} />
            </>
          )}
          <SectionWrapper>
            <div ref={documentVerificationSectionRefs?.current[DocumentsSection.General]}>
              <DocumentVerificationTemplateHeadSection
                vtmplVersion={vtmplVersion}
                linkedStepCallout={<StepReferences> {linkedStepsElement}</StepReferences>}
                editable={editableUngated}
              />
            </div>
            {(editableInternal || canViewDocumentExtractionConfig) && (
              <>
                <div
                  ref={documentVerificationSectionRefs?.current[DocumentsSection.Classifications]}
                >
                  <ClassificationsConfig
                    classifications={classifications}
                    selected={getSelectedIndex(DocumentsSection.Classifications)}
                    accepted={acceptedClassifications}
                    onReplace={handleReplaceClassifications}
                    onSelect={(index) => handleOnSelect(DocumentsSection.Classifications, index)}
                    onToggleAccepted={handleToggleClassificationAccepted}
                    onDelete={handleDeleteClassification}
                    editable={editable}
                  />
                </div>
                <div ref={documentVerificationSectionRefs?.current[DocumentsSection.Extractions]}>
                  <ExtractionsTableWithMultipleFieldWritebacks
                    extractions={extractions}
                    requiredExtractionsFieldNames={requiredExtractionsFieldNames}
                    selected={getSelectedIndex(DocumentsSection.Extractions)}
                    onSelect={(index) => handleOnSelect(DocumentsSection.Extractions, index)}
                    onDelete={handleDeleteExtraction}
                    fieldWriteBacks={fieldWriteBacks}
                    editable={editable}
                    hasInquiryTemplate={hasInquiryTemplate}
                  />
                </div>
                <div ref={documentVerificationSectionRefs?.current[DocumentsSection.Insights]}>
                  <InsightsTable
                    insights={insights}
                    selected={getSelectedIndex(DocumentsSection.Insights)}
                    onSelect={(index) => handleOnSelect(DocumentsSection.Insights, index)}
                    onDelete={handleDeleteInsight}
                    fieldWriteBacks={fieldWriteBacks}
                    insightLimit={insightLimit}
                    aiLimitReached={blockDocumentAiFeatures(vtmplVersion)}
                    editable={editable}
                  />
                </div>
                <div
                  ref={documentVerificationSectionRefs?.current[DocumentsSection.DocumentFields]}
                >
                  <DocumentFields
                    documentFields={documentFields}
                    selected={getSelectedIndex(DocumentsSection.DocumentFields)}
                    onSelect={(index) => handleOnSelect(DocumentsSection.DocumentFields, index)}
                    onDelete={handleDeleteDocumentField}
                    editable={editable}
                  />
                </div>
              </>
            )}
            <div
              ref={documentVerificationSectionRefs?.current[DocumentsSection.VerificationChecks]}
            >
              <DocumentFormVerificationTemplateChecks
                vtmplVersion={vtmplVersion}
                editable={canEditVerificationChecks}
                matchRequirementProfiles={matchRequirementProfiles}
                selectedMatchRequirement={selectedMatchRequirement}
                scrollToDocumentsSection={scrollToDocumentsSection}
              />
            </div>
          </SectionWrapper>
        </FormSection>
      </RightColumn>
      <ControlPanelContainer visible={shouldDisplayDocumentPanel}>
        {shouldDisplayDocumentPanel && documentPanel?.type === DocumentsSection.DocumentFields && (
          <DocumentFieldPanel
            key={documentPanel.index}
            vtmplVersion={vtmplVersion}
            documentFieldIndex={documentPanel.index}
            closeDocumentField={() => handleCloseDocumentPanel()}
            addDocumentField={handleAddDocumentField}
            updateDocumentField={handleUpdateDocumentField}
            deleteDocumentField={() => handleDeleteDocumentField(documentPanel.index!)}
          />
        )}
        {shouldDisplayDocumentPanel && documentPanel?.type === DocumentsSection.Classifications && (
          <ClassificationPanel
            key={documentPanel.index}
            vtmplVersion={vtmplVersion}
            selected={documentPanel.index}
            onClose={() => handleCloseDocumentPanel()}
            onAdd={handleAddClassification}
            onUpdate={handleUpdateClassification}
            onDelete={() => handleDeleteClassification(documentPanel.index!)}
          />
        )}
        {shouldDisplayDocumentPanel && documentPanel?.type === DocumentsSection.Extractions && (
          <ExtractionPanel
            key={documentPanel.index}
            vtmplVersion={vtmplVersion}
            extractionIndex={documentPanel.index}
            fieldSchema={fieldSchema ? fieldSchema : []}
            closeExtraction={() => handleCloseDocumentPanel()}
            addExtraction={handleAddExtraction}
            updateExtraction={handleUpdateExtraction}
            deleteExtraction={() => handleDeleteExtraction(documentPanel.index!)}
            aiLimitReached={blockDocumentAiFeatures(vtmplVersion)}
            hasInquiryTemplate={hasInquiryTemplate}
          />
        )}
        {shouldDisplayDocumentPanel && documentPanel?.type === DocumentsSection.Insights && (
          <InsightPanel
            key={documentPanel.index}
            vtmplVersion={vtmplVersion}
            insightIndex={documentPanel.index}
            closeInsight={() => handleCloseDocumentPanel()}
            addInsight={handleAddInsight}
            updateInsight={handleUpdateInsight}
            deleteInsight={() => handleDeleteInsight(documentPanel.index!)}
          />
        )}
        {shouldDisplayDocumentPanel && documentPanel?.type === DocumentsSection.DocumentTester && (
          <FeatureFlagged featureFlag='dashboard:fle-enable-document-tester'>
            <DocumentTesterPanel onClose={handleCloseDocumentPanel} />
          </FeatureFlagged>
        )}
      </ControlPanelContainer>
    </>
  );
};

export default FormVerificationDocumentTemplateConfig;
